/**
 * Custom Encrypted Node for Lexical
 * A decorator node for encrypted content sections that renders a React component
 */

import {
  $applyNodeReplacement,
  DOMConversionMap,
  DOMExportOutput,
  DecoratorNode,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical'
import { ReactNode } from 'react'

export type SerializedCustomEncryptedNode = Spread<
  {
    encryptionId: string
    encryptedText: string
    algo: 'aes-gcm'
    version: 1
    character?: string
    authorHeld?: true
  },
  SerializedLexicalNode
>

export class CustomEncryptedNode extends DecoratorNode<ReactNode> {
  __encryptionId: string
  __encryptedText: string
  __algo: 'aes-gcm'
  __version: 1
  /** Character tag this passage is about; absent for sentence-locked passages. */
  __character?: string
  /** The author holds this one: no sentence, opened by his own hand. */
  __authorHeld?: true

  static getType(): string {
    return 'custom-encrypted'
  }

  static clone(node: CustomEncryptedNode): CustomEncryptedNode {
    return new CustomEncryptedNode(
      node.__encryptionId,
      node.__encryptedText,
      node.__algo,
      node.__version,
      node.__key,
      node.__character,
      node.__authorHeld,
    )
  }

  constructor(
    encryptionId: string,
    encryptedText: string = '',
    algo: 'aes-gcm' = 'aes-gcm',
    version: 1 = 1,
    key?: NodeKey,
    character?: string,
    authorHeld?: true,
  ) {
    super(key)
    this.__encryptionId = encryptionId
    this.__encryptedText = encryptedText
    this.__algo = algo
    this.__version = version
    this.__character = character
    this.__authorHeld = authorHeld
  }

  getCharacter(): string | undefined {
    return this.__character
  }

  getAuthorHeld(): true | undefined {
    return this.__authorHeld
  }

  setCharacter(character: string | undefined): this {
    const writable = this.getWritable()
    writable.__character = character
    return writable
  }

  getEncryptionId(): string {
    return this.__encryptionId
  }

  setEncryptionId(encryptionId: string): this {
    const writable = this.getWritable()
    writable.__encryptionId = encryptionId
    return writable
  }

  getEncryptedText(): string {
    return this.__encryptedText
  }

  setEncryptedText(text: string): this {
    const writable = this.getWritable()
    writable.__encryptedText = text
    return writable
  }

  getAlgo(): 'aes-gcm' {
    return this.__algo
  }

  getVersion(): 1 {
    return this.__version
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const span = document.createElement('span')
    span.className = 'encrypted-content-wrapper'
    span.style.display = 'inline'
    return span
  }

  updateDOM(): boolean {
    return false
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (node: Node) => {
        const element = node as HTMLElement
        if (!element.classList.contains('encrypted-content')) {
          return null
        }
        return {
          conversion: (element: HTMLElement) => {
            const encryptionId = element.getAttribute('data-section-id') || ''
            const algo = (element.getAttribute('data-algo') as 'aes-gcm') || 'aes-gcm'
            const version = parseInt(element.getAttribute('data-version') || '1', 10) as 1
            const encryptedText = element.textContent || ''
            return {
              node: $createCustomEncryptedNode(encryptionId, encryptedText, algo, version),
            }
          },
          priority: 2,
        }
      },
    }
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('span')
    element.className = 'encrypted-content'
    element.setAttribute('data-section-id', this.__encryptionId)
    element.setAttribute('data-algo', this.__algo)
    element.setAttribute('data-version', String(this.__version))
    element.textContent = this.__encryptedText
    return { element }
  }

  static importJSON(serializedNode: SerializedCustomEncryptedNode): CustomEncryptedNode {
    return $createCustomEncryptedNode(
      serializedNode.encryptionId,
      serializedNode.encryptedText,
      serializedNode.algo,
      serializedNode.version,
      serializedNode.character,
      serializedNode.authorHeld,
    )
  }

  exportJSON(): SerializedCustomEncryptedNode {
    return {
      type: 'custom-encrypted',
      version: 1,
      encryptionId: this.__encryptionId,
      encryptedText: this.__encryptedText,
      algo: this.__algo,
      ...(this.__character ? { character: this.__character } : {}),
      ...(this.__authorHeld ? { authorHeld: true as const } : {}),
    }
  }

  getTextContent(): string {
    return this.__encryptedText
  }

  isInline(): boolean {
    return true
  }

  // DecoratorNode requires this method to return a React component
  // The actual rendering is handled by EncryptedContentPlugin which provides the context
  decorate(): ReactNode {
    // Return null here - the actual component will be rendered by the plugin
    // which has access to mnemonicMap and other context
    return null
  }
}

export function $createCustomEncryptedNode(
  encryptionId: string,
  encryptedText: string = '',
  algo: 'aes-gcm' = 'aes-gcm',
  version: 1 = 1,
  character?: string,
  authorHeld?: true,
): CustomEncryptedNode {
  return $applyNodeReplacement(
    new CustomEncryptedNode(encryptionId, encryptedText, algo, version, undefined, character, authorHeld),
  )
}

export function $isCustomEncryptedNode(node: LexicalNode | null | undefined): node is CustomEncryptedNode {
  return node instanceof CustomEncryptedNode
}
